import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { format } from 'date-fns';
import { ApolloError } from 'apollo-client';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CoachingConstants, { CoachingSessionDuration } from 'coaching/CoachingConstants';
import DateTimeAvailabilitySelect from 'components/DateTimeAvailabilitySelect';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Icon from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator';
import NoCoachAvailabilityNullState from 'coaching/components/CoachingHomeView/NoCoachAvailabilityNullState';
import Row, { RowSize } from 'components/Row';
import Text, { TextSize } from 'components/Text';
import { DurationSelectRow } from 'coaching/components/CoachingHomeView/BookSessionSelectRows';
import { getDatesFromReservations } from 'coaching/lib/timeIntervals';
import {
  CoachingSessionSlot,
  CoachingTimeOff,
} from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { CoachingEventDefinitionInput } from 'globalTypes';

export type Props = {
  startDateTimeRange: string;
  endDateTimeRange: string;
  weekOffset: number;
  timezone: string;
  sessionSlots?: CoachingSessionSlot[];
  timeOff?: CoachingTimeOff[];
  onSelectSlot: (input?: CoachingEventDefinitionInput) => void;
  onWeekForward: () => void;
  onWeekBack: () => void;
  eventPrefix?: string;
  loading: boolean;
  error?: ApolloError;
};

export default function BookOneOffSession(props: Props) {
  const {
    startDateTimeRange,
    endDateTimeRange,
    weekOffset,
    sessionSlots,
    timezone,
    onSelectSlot,
    onWeekBack,
    onWeekForward,
    eventPrefix,
    loading,
    error,
  } = props;
  function trackKey(key: string, properties?: Record<string, any>) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`, properties);
  }
  function trackShowAvailabilityToggle() {
    trackKey('show_availability.toggle');
  }

  const [selectedStartDateTime, setSelectedStartDateTime] = useState<moment.Moment>();
  const [selectedDuration, setSelectedDuration] = useState<CoachingSessionDuration>(
    CoachingConstants.COACHING_SESSION_DURATIONS[0]
  );

  useEffect(() => {
    trackKey('view');
  }, []);

  useEffect(() => {
    onSelectSlot(undefined);
  }, [selectedStartDateTime]);

  useEffect(() => {
    // if the slot just got unselected, no need to do anything else
    if (!selectedStartDateTime) {
      return;
    }

    const proposedEventDefinition = generateEventDefinition(
      selectedDuration,
      selectedStartDateTime,
      timezone
    );
    onSelectSlot(proposedEventDefinition);
  }, [selectedStartDateTime, selectedDuration, timezone]);

  if (loading) {
    return (
      <BoxBody size="small">
        <Text align="center">
          <LoadingIndicator data-test="loading-indicator" />
        </Text>
      </BoxBody>
    );
  }

  if (error || !sessionSlots) {
    return <NoCoachAvailabilityNullState />;
  }

  const startDateTimeOptions = sessionSlots.map(s => s.startDateTime);
  const daysToShow = getDatesFromReservations(startDateTimeOptions);

  return (
    <>
      <BoxBody size="small">
        <WeekTraversalHeader
          onWeekBack={() => {
            setSelectedStartDateTime(undefined);
            onWeekBack();
            trackKey('previous_week.click');
          }}
          onWeekForward={() => {
            setSelectedStartDateTime(undefined);
            onWeekForward();
            trackKey('next_week.click');
          }}
          weekOffset={weekOffset}
          startDateTimeRange={startDateTimeRange}
          endDateTimeRange={endDateTimeRange}
        />
        <DateTimeAvailabilitySelect
          daysPerRow={4}
          slotsPerRow={2}
          daysToShow={daysToShow}
          dateTimeOptions={startDateTimeOptions}
          onChange={slot => {
            setSelectedStartDateTime(slot);
            trackKey('time.click', { data: slot.toISOString() });
          }}
          value={selectedStartDateTime}
          onSelectDay={day => {
            setSelectedStartDateTime(undefined);
            trackKey('date.click', { data: day.format('YYYY-MM-DD') });
          }}
          emptyText={<NoCoachAvailabilityNullState />}
          onMoreAvailabilities={trackShowAvailabilityToggle}
          onLessAvailabilities={trackShowAvailabilityToggle}
        />
        <Divider />
        <DurationSelectRow
          selectedDuration={selectedDuration}
          onSelectDuration={duration => {
            setSelectedDuration(duration);
            trackKey('length.click');
          }}
        />
      </BoxBody>
    </>
  );
}

export function WeekTraversalHeader({
  onWeekBack,
  onWeekForward,
  startDateTimeRange,
  endDateTimeRange,
  weekOffset,
  maxOffset,
  buttonSize = 'xxxsmall',
  timeRangeTextSize = 'xxsmall',
  size = 'small',
}: {
  startDateTimeRange: string;
  endDateTimeRange: string;
  weekOffset: number;
  maxOffset?: number;
  onWeekForward: () => void;
  onWeekBack: () => void;
  buttonSize?: TextSize;
  timeRangeTextSize?: TextSize;
  size?: RowSize;
}) {
  return (
    <Row size={size}>
      <Buttons fullButtonIndex={1} reset="none">
        <Button
          data-test="week-offset-back-arrow"
          level="icon"
          color="graphite"
          onClick={onWeekBack}
          disabled={weekOffset <= 0}>
          <Text size={buttonSize} align="left">
            <Icon size="inherit" name="chevron-left" />
          </Text>
        </Button>
        <Text
          data-test="week-time-range-text"
          size={timeRangeTextSize}
          color="graphite"
          align="center">
          {format(startDateTimeRange, 'MMM D')} - {format(endDateTimeRange, 'MMM D')}
        </Text>
        <Button
          data-test="week-offset-forward-arrow"
          level="icon"
          color="graphite"
          onClick={onWeekForward}
          disabled={maxOffset !== undefined ? weekOffset >= maxOffset : false}>
          <Text size={buttonSize} align="right">
            <Icon size="inherit" name="chevron-right" />
          </Text>
        </Button>
      </Buttons>
    </Row>
  );
}

function generateEventDefinition(
  duration: CoachingSessionDuration,
  startDateTime: moment.Moment,
  timezone: string
) {
  return {
    startDateTime: startDateTime.toISOString(),
    endDateTime: moment
      .utc(startDateTime)
      .minutes(duration)
      .toISOString(),
    recurrence: [],
    timezone,
  };
}
